import { forwardRef } from "react";

import { cn } from "../../../lib/utils";
import "./ArrowFillButton.css";

/**
 * ArrowFillButton - the Mission page's primary action.
 *
 * WHAT THIS IS. The Obsidian Arrow Fill button's APPEARANCE, adapted onto this
 * project's existing button contract. It is a plain `<button>`: the caller's
 * own `type`, `onClick`, `disabled` and children flow straight through, and
 * nothing here intercepts a form submit.
 *
 * WHY `{...rest}` COMES AFTER `type=`. Exactly as upstream does it. A caller
 * passing `type="submit"` must win over the default, because the Mission page's
 * CTA IS the submit control of its own form and its entire completion cascade
 * (setPoint, setStep, onStepComplete, the read_objective POST) hangs off that
 * form's onSubmit. Plan F4 corrected an earlier claim that upstream forced the
 * type; it does not, and neither does this. Verified in the rendered DOM, not
 * assumed from either source.
 *
 * ONE LABEL, NOT TWO (stage 4 fix 1). Upstream duplicates the label inside the
 * fill layer for width stability and stage 1 copied that. The fill is
 * `position: absolute` here, so its contents never affected the button's width:
 * measured 198px with the duplicate and 198px without. Removing it also fixed
 * the real defect, which the duplicate was masking rather than solving. See
 * ArrowFillButton.css.
 *
 * NO PATH ALIAS IN THIS PROJECT (plan F4a). This is plain un-ejected
 * react-scripts with no `jsconfig`/`tsconfig` `paths` entry, so the upstream
 * convention `import { cn } from "@/lib/utils"` would not resolve. `cn` already
 * exists at `src/lib/utils.js` and is imported relatively above. Do not add an
 * alias for this component.
 *
 * STAGE 2 wired the fill sweep and the arrow travel. STAGE 4 fixed the
 * foreground: the label and arrow flip to navy as the lime panel arrives.
 *
 * F01 (student frontend elevation) made this the shared action primitive:
 *   - `tone="mission" | "dark"`: mission is the navy pill with the lime sweep
 *     for the pear Mission surface; dark is the pear pill for dark surfaces.
 *   - `arrow`: forward actions keep the arrow; a save passes `arrow={false}` so
 *     it never looks like navigation.
 *   - `size="compact" | "standard"`: compact is the Mission CTA's reviewed
 *     16px/600 face; standard is the brand 20px/500 button, taken from the one
 *     scoped `xe-action-label` role in experienceElevation/presentation.css.
 *   Defaults (mission, arrow, compact) render Mission's existing DOM exactly:
 *   no extra class, the same three children.
 *   - The ref is forwarded to the `<button>`, and every other prop (type,
 *     disabled, aria-*, data-*, handlers) still spreads after `type=`.
 *   - Pending belongs to the caller: pass the pending label as children and
 *     `aria-busy="true"` (usually with `disabled`). The CSS keeps that label
 *     readable and suppresses the sweep; there is still exactly one label node.
 *   - The label renders uppercase through CSS only (E33). Credential surfaces
 *     keep their ratified sentence case by adding `afb-case-verbatim`.
 */
export const ArrowFillButton = forwardRef(function ArrowFillButton(
  {
    children,
    className,
    disabled = false,
    tone = "mission",
    arrow = true,
    size = "compact",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn("afb-root", tone === "dark" && "afb-root--dark", className)}
      disabled={disabled}
      {...rest}
    >
      <span className="afb-fill" aria-hidden="true" />
      <span
        className={cn("afb-label", size === "standard" && "xe-action-label")}
      >
        {children}
      </span>
      {arrow && <ArrowGlyph />}
    </button>
  );
});

/**
 * The arrow, inline rather than an imported asset on purpose: under CRA's jest
 * transform an imported SVG resolves to its FILENAME and renders as text, which
 * would put `arrow.svg` into the button's textContent and into any assertion
 * written over it.
 */
const ArrowGlyph = () => (
  <svg
    className="afb-arrow"
    width="17"
    height="12"
    viewBox="0 0 17 12"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M1 6h14M10.5 1.5 15.5 6l-5 4.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default ArrowFillButton;
